/**
 * NOTE: This code belongs to Kangaroo Rewards. Unauthorized use is prohibited
 */

@file:Suppress("unused", "MemberVisibilityCanBePrivate", "EXPERIMENTAL_API_USAGE")
@file:OptIn(ExperimentalJsExport::class)
package features.orderStatus

import kangaroorewards.appsdk.core.api.Api
import kangaroorewards.appsdk.core.domain.Result
import kangaroorewards.appsdk.core.domain.Result.Idle
import kangaroorewards.appsdk.core.domain.Result.Loading
import kangaroorewards.appsdk.core.domain.SerializedResult
import kangaroorewards.appsdk.core.domain.toJsonResult
import kangaroorewards.appsdk.core.utils.CFlow
import kangaroorewards.appsdk.core.utils.asCFlow
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.mapLatest
import kotlinx.serialization.InternalSerializationApi
import kotlin.js.ExperimentalJsExport
import features.orderStatus.internal.OrderStatusUseCase
import features.orderStatus.internal.OrderStatusUseCase.Params
import features.orderStatus.models.OrderStatusResponseModel


typealias OrderStatusState = CFlow<OrderStatusUseCaseResult?>

typealias OrderStatusUseCaseResult = Result<OrderStatusResponseModel>

fun OrderStatusUseCaseResult.serializeOrderStatusApiResult(): SerializedResult<String> {
    return this.toJsonResult<OrderStatusResponseModel>()
}

/**
 * Serializes [OrderStatusState]'s data and returns a new
 * [CFlow] containing data serialized as a JSON [String].
 */
@OptIn(ExperimentalCoroutinesApi::class)
fun OrderStatusState.serializeOrderStatusState(): CFlow<SerializedResult<String>?> {
    return this.mapLatest {
        it?.toJsonResult<OrderStatusResponseModel>()
    }.asCFlow()
}

/**
 * Get a list of messages for the order status [Api] class
 */
class OrderStatusApi : Api() {

    val orderStatusState: OrderStatusState
        get() = _orderStatusState.asCFlow()

    companion object {
        private val _orderStatusState: MutableStateFlow<OrderStatusUseCaseResult> = MutableStateFlow(Idle())
    }

    private val orderStatusUseCase = OrderStatusUseCase()

    /**
     * Get a list of messages for the order status
     */
    suspend fun getOrderStatus(
        overrideHeaders: Map<String, String>? = null,
        businessId: String
    ): Result<OrderStatusResponseModel> {
        /* set state to Loading as soon as function is called */
        _orderStatusState.value = Loading()
        val result = this.orderStatusUseCase(
            Params(
                overrideHeaders = overrideHeaders,
                businessId = businessId
            )
        ) { result ->
            _orderStatusState.value = result
            return@orderStatusUseCase result
        }
        return result
    }

}
